import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Copy, Calendar, CheckCircle2 } from "lucide-react";
import { api, saveContent, API } from "../../lib/api";
import { calendarStatus, connectCalendar, disconnectCalendar } from "../crm/crmApi";
import { Field } from "./AdminPrimitives";

const WEBHOOK_URL = `${API}/webhook/whatsapp`;

// One-time OAuth connect for the shared Google Workspace calendar account.
const GoogleCalendarConnect = ({ content }) => {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try { setStatus(await calendarStatus()); } catch { setStatus({ configured: false, connected: false }); }
    };

    useEffect(() => {
        let active = true;
        (async () => {
            try { const s = await calendarStatus(); if (active) setStatus(s); }
            catch { if (active) setStatus({ configured: false, connected: false }); }
        })();
        const params = new URLSearchParams(window.location.search);
        const g = params.get("gcal");
        if (g === "connected") toast.success("Google Calendar connected.");
        else if (g === "error") toast.error("Google Calendar connection failed. Check the Client ID/Secret and redirect URI.");
        if (g) { params.delete("gcal"); window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? "?" + params : ""}`); }
        return () => { active = false; };
    }, []);

    const connect = async () => {
        setBusy(true);
        try {
            await saveContent(content); // persist the just-entered client id/secret first
            const { url } = await connectCalendar(window.location.origin);
            window.location.href = url; // full-page redirect to Google consent
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start Google connection.");
            setBusy(false);
        }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect Google Calendar? New follow-up invites will be blocked until reconnected.")) return;
        setBusy(true);
        try { await disconnectCalendar(); toast.success("Google Calendar disconnected."); await load(); }
        catch { toast.error("Could not disconnect."); }
        finally { setBusy(false); }
    };

    return (
        <div className="mt-3 rounded-2xl border border-oth-line/70 bg-white p-4" data-testid="gcal-connect-box">
            {status?.connected ? (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-sm font-body text-oth-ink">
                        <CheckCircle2 size={18} className="text-[#0B6B3A]" />
                        <span>Connected as <span className="font-semibold">{status.email}</span></span>
                    </div>
                    <button type="button" onClick={disconnect} disabled={busy} className="text-[13px] font-body text-red-600 hover:underline disabled:opacity-50" data-testid="gcal-disconnect">Disconnect</button>
                </div>
            ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm font-body text-oth-ink2">
                        {status?.configured
                            ? "Credentials saved. Connect the calendar account to start sending invites."
                            : "Enter the Client ID & Secret above, then connect."}
                    </div>
                    <button type="button" onClick={connect} disabled={busy} className="btn-pill btn-pill-dark text-sm" data-testid="gcal-connect">
                        <Calendar size={14} /> {busy ? "Opening Google…" : "Connect Google Calendar"}
                    </button>
                </div>
            )}
        </div>
    );
};

export const IntegrationsTab = ({ content, update }) => {
    const sendTestEmail = async () => {
        try {
            // Ensure latest SMTP creds are persisted before testing
            await saveContent(content);
            const r = await api.post("/admin/test-email");
            toast.success(`Test email sent to ${r.data.sent_to}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test email failed");
        }
    };

    return (
        <div>
            <div className="mb-6 p-5 rounded-2xl bg-[#0A0A0A] text-white/85 font-body text-[13px] leading-relaxed" data-testid="integrations-instructions">
                <div className="font-display text-base font-semibold text-white mb-2">Setup instructions</div>
                <div className="space-y-3">
                    <div>
                        <span className="text-white font-medium">Google Analytics (GA4):</span>{" "}
                        Go to{" "}
                        <a href="https://analytics.google.com" target="_blank" rel="noreferrer" className="underline text-white">analytics.google.com</a>
                        {" "}→ create a property → Data Streams → Web → copy the <span className="text-white">Measurement ID</span> (looks like <span className="text-white">G-XXXXXXXXXX</span>) and paste it below.
                    </div>
                    <div>
                        <span className="text-white font-medium">Google reCAPTCHA v3:</span>{" "}
                        Go to{" "}
                        <a href="https://www.google.com/recaptcha/admin/create" target="_blank" rel="noreferrer" className="underline text-white">google.com/recaptcha/admin/create</a>
                        {" "}→ choose <span className="text-white">reCAPTCHA v3</span> → add your domains (including the preview domain) → submit → copy both the <span className="text-white">Site key</span> and the <span className="text-white">Secret key</span> here.
                    </div>
                    <div className="text-white/65">
                        Both fields are optional. If left empty, the public site loads as usual without analytics or captcha. Toggle reCAPTCHA on only after both keys are saved.
                    </div>
                </div>
            </div>

            <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Google Analytics</div>
            <Field
                label="Measurement ID"
                value={content.integrations?.google_analytics_id}
                onChange={(v) => update("integrations.google_analytics_id", v)}
                hint="Format: G-XXXXXXXXXX. Leave empty to disable analytics."
                testId="ga-id"
            />

            <div className="mt-10 text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">reCAPTCHA v3</div>
            <Field
                label="Site key (public)"
                value={content.integrations?.recaptcha_site_key}
                onChange={(v) => update("integrations.recaptcha_site_key", v)}
                hint="Safe to expose in the browser; sent to Google when a visitor submits the contact form."
                testId="captcha-site-key"
            />
            <Field
                label="Secret key (server-side)"
                value={content.integrations?.recaptcha_secret_key}
                onChange={(v) => update("integrations.recaptcha_secret_key", v)}
                hint="Used by the backend to verify the captcha token with Google. Never shown publicly."
                testId="captcha-secret-key"
            />

            <label className="flex items-center gap-3 mt-4 cursor-pointer" data-testid="captcha-enabled-row">
                <input
                    type="checkbox"
                    checked={Boolean(content.integrations?.recaptcha_enabled)}
                    onChange={(e) => update("integrations.recaptcha_enabled", e.target.checked)}
                    className="w-4 h-4 accent-[#0B3B60]"
                    data-testid="captcha-enabled-toggle"
                />
                <span className="font-body text-sm text-oth-ink">Enable reCAPTCHA on the Contact Us form</span>
            </label>

            <Field
                label="Minimum score (0.0 – 1.0)"
                value={String(content.integrations?.recaptcha_min_score ?? 0.5)}
                onChange={(v) => {
                    const n = parseFloat(v);
                    update("integrations.recaptcha_min_score", isNaN(n) ? 0.5 : Math.min(1, Math.max(0, n)));
                }}
                hint="reCAPTCHA v3 returns a score. 0.5 is a safe default. Raise to 0.7 if you see spam."
                testId="captcha-min-score"
            />

            <div className="mt-10 text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Email Notifications (Gmail SMTP)</div>
            <div className="mb-4 p-4 rounded-2xl bg-[#0A0A0A] text-white/85 font-body text-[13px] leading-relaxed" data-testid="smtp-instructions">
                <span className="text-white font-medium">Setup:</span>{" "}
                Sign in to the <span className="text-white">From address</span> mailbox at{" "}
                <a href="https://accounts.google.com" target="_blank" rel="noreferrer" className="underline text-white">accounts.google.com</a>
                {" "}→ enable 2-Step Verification → visit{" "}
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline text-white">myaccount.google.com/apppasswords</a>
                {" "}→ generate an App Password for &quot;Mail&quot; → paste the 16-character password below. Spaces in the password are fine.
            </div>

            <Field
                label="From address (Gmail / Google Workspace mailbox)"
                value={content.integrations?.smtp_user}
                onChange={(v) => update("integrations.smtp_user", v)}
                hint="Notifications will be sent from this Google account. Must have 2-Step Verification enabled."
                testId="smtp-user"
            />
            <Field
                label="App Password (16 characters)"
                type="password"
                value={content.integrations?.smtp_password}
                onChange={(v) => update("integrations.smtp_password", v)}
                hint="Generated via myaccount.google.com/apppasswords. Saved encrypted at rest is recommended in production — currently stored in CMS DB."
                testId="smtp-password"
            />
            <Field
                label="From name (shown to recipient)"
                value={content.integrations?.smtp_from_name}
                onChange={(v) => update("integrations.smtp_from_name", v)}
                hint='Display name on the email, e.g. "One Tree Hill Prime Wealth".'
                testId="smtp-from-name"
            />
            <Field
                label="Notification recipient email"
                value={content.integrations?.notification_email}
                onChange={(v) => update("integrations.notification_email", v)}
                hint="Every new contact-form submission will be emailed here."
                testId="notif-email"
            />

            <label className="flex items-center gap-3 mt-4 cursor-pointer" data-testid="notif-enabled-row">
                <input
                    type="checkbox"
                    checked={Boolean(content.integrations?.notification_enabled)}
                    onChange={(e) => update("integrations.notification_enabled", e.target.checked)}
                    className="w-4 h-4 accent-[#0B3B60]"
                    data-testid="notif-enabled-toggle"
                />
                <span className="font-body text-sm text-oth-ink">Send email notification for every new contact enquiry</span>
            </label>

            <button
                type="button"
                onClick={sendTestEmail}
                className="btn-pill btn-pill-dark text-sm mt-6"
                data-testid="send-test-email-btn"
            >
                Save & send test email <ArrowUpRight size={14} />
            </button>

            <div className="mt-12 text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">WhatsApp Business (Meta Cloud API)</div>
            <div className="mb-4 p-4 rounded-2xl bg-[#0A0A0A] text-white/85 font-body text-[13px] leading-relaxed" data-testid="whatsapp-instructions">
                <div className="font-display text-base font-semibold text-white mb-2">Setup instructions</div>
                <div className="space-y-2">
                    <div>1. Create an app at <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="underline text-white">developers.facebook.com</a> → add the <span className="text-white">WhatsApp</span> product → connect your WhatsApp Business Account (WABA) and a phone number.</div>
                    <div>2. In <span className="text-white">Business Settings → System Users</span>, generate a <span className="text-white">permanent access token</span> with <span className="text-white">whatsapp_business_messaging</span> + <span className="text-white">whatsapp_business_management</span> permissions.</div>
                    <div>3. Copy the <span className="text-white">Phone Number ID</span>, <span className="text-white">WABA ID</span> (WhatsApp → API Setup) and the <span className="text-white">App Secret</span> (App → Settings → Basic).</div>
                    <div>4. In <span className="text-white">WhatsApp → Configuration</span>, set the Callback URL below + your chosen Verify Token, then subscribe to the <span className="text-white">messages</span> field.</div>
                </div>
            </div>

            <div className="mb-5">
                <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2">Webhook Callback URL (paste into Meta)</div>
                <div className="flex items-center gap-2">
                    <input readOnly value={WEBHOOK_URL} className="flex-1 bg-[#F5F5F2] border border-oth-line/70 rounded-2xl px-4 py-3 font-mono text-[12px] text-oth-ink2" data-testid="whatsapp-webhook-url" />
                    <button type="button" onClick={() => { navigator.clipboard?.writeText(WEBHOOK_URL); toast.success("Webhook URL copied"); }} className="w-12 h-12 rounded-2xl border border-oth-line/70 bg-white flex items-center justify-center text-oth-ink2 hover:bg-[#F5F5F2]" data-testid="whatsapp-webhook-copy"><Copy size={16} /></button>
                </div>
            </div>

            <label className="flex items-center gap-3 mb-5 cursor-pointer" data-testid="whatsapp-enabled-row">
                <input
                    type="checkbox"
                    checked={Boolean(content.integrations?.whatsapp_enabled)}
                    onChange={(e) => update("integrations.whatsapp_enabled", e.target.checked)}
                    className="w-4 h-4 accent-[#0B3B60]"
                    data-testid="whatsapp-enabled-toggle"
                />
                <span className="font-body text-sm text-oth-ink">Enable WhatsApp sending & inbound lead capture</span>
            </label>

            <Field label="Permanent Access Token" type="password" value={content.integrations?.whatsapp_access_token} onChange={(v) => update("integrations.whatsapp_access_token", v)} hint="System-user token. Stored server-side; never exposed publicly." testId="whatsapp-access-token" />
            <Field label="Phone Number ID" value={content.integrations?.whatsapp_phone_number_id} onChange={(v) => update("integrations.whatsapp_phone_number_id", v)} hint="From WhatsApp → API Setup (numeric)." testId="whatsapp-phone-id" />
            <Field label="WhatsApp Business Account ID (WABA)" value={content.integrations?.whatsapp_waba_id} onChange={(v) => update("integrations.whatsapp_waba_id", v)} testId="whatsapp-waba-id" />
            <Field label="App Secret" type="password" value={content.integrations?.whatsapp_app_secret} onChange={(v) => update("integrations.whatsapp_app_secret", v)} hint="Used to verify inbound webhook signatures." testId="whatsapp-app-secret" />
            {!content.integrations?.whatsapp_app_secret && (
                <div className="-mt-2 mb-5 text-[12px] font-body text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="whatsapp-secret-warning">
                    ⚠ Inbound webhook signature checking is disabled until an App Secret is set. Add it before going live.
                </div>
            )}
            <Field label="Webhook Verify Token" value={content.integrations?.whatsapp_verify_token} onChange={(v) => update("integrations.whatsapp_verify_token", v)} hint="Any secret string you choose; must match what you enter in Meta." testId="whatsapp-verify-token" />
            <Field label="Graph API Version" value={content.integrations?.whatsapp_graph_version} onChange={(v) => update("integrations.whatsapp_graph_version", v)} hint="Default v21.0." testId="whatsapp-graph-version" />

            <div className="mt-12 text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Google Calendar (follow-up invites)</div>
            <div className="mb-4 p-4 rounded-2xl bg-[#0A0A0A] text-white/85 font-body text-[13px] leading-relaxed" data-testid="gcal-instructions">
                <div className="font-display text-base font-semibold text-white mb-2">Setup instructions</div>
                <div className="space-y-2">
                    <div>1. In <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="underline text-white">Google Cloud Console</a> → <span className="text-white">APIs &amp; Services → Library</span> → enable <span className="text-white">Google Calendar API</span>.</div>
                    <div>2. <span className="text-white">OAuth consent screen</span> → choose <span className="text-white">Internal</span> (you are on Workspace).</div>
                    <div>3. <span className="text-white">Credentials → Create credentials → OAuth client ID → Web application</span>. Add this exact <span className="text-white">Authorized redirect URI</span>:</div>
                    <div className="flex items-center gap-2 mt-1">
                        <input readOnly value={`${API}/oauth/calendar/callback`} className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 font-mono text-[12px] text-white" data-testid="gcal-redirect-uri" />
                        <button type="button" onClick={() => { navigator.clipboard?.writeText(`${API}/oauth/calendar/callback`); toast.success("Redirect URI copied"); }} className="w-10 h-10 rounded-xl border border-white/20 bg-white/10 flex items-center justify-center text-white hover:bg-white/20"><Copy size={15} /></button>
                    </div>
                    <div>4. Copy the <span className="text-white">Client ID</span> and <span className="text-white">Client secret</span> into the fields below, Save, then click <span className="text-white">Connect Google Calendar</span>. Invites are created on this account&apos;s calendar in <span className="text-white">IST</span>.</div>
                </div>
            </div>

            <Field label="OAuth Client ID" value={content.integrations?.google_client_id} onChange={(v) => update("integrations.google_client_id", v)} hint="From Google Cloud Console → Credentials → OAuth client ID (Web application)." testId="gcal-client-id" />
            <Field label="OAuth Client Secret" type="password" value={content.integrations?.google_client_secret} onChange={(v) => update("integrations.google_client_secret", v)} hint="Stored server-side; never exposed publicly." testId="gcal-client-secret" />
            <GoogleCalendarConnect content={content} />
        </div>
    );
};
